import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  scrollProgress: number;
}

const Navbar: React.FC<NavbarProps> = ({ scrollProgress }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="fixed top-2 sm:top-4 left-1/2 transform -translate-x-1/2 z-50 w-[95%] max-w-6xl">
      <div className="bg-white/90 backdrop-blur-xl border border-white/20 rounded-xl sm:rounded-2xl">
        <div className="flex items-center justify-between h-16 sm:h-20 px-4 sm:px-6">
          {/* Logo */}
          <div className="flex items-center">
            <img src="/images/updated-05.png" alt="Curiye Advert" className="h-8 sm:h-10 md:h-12 w-auto transition-transform duration-300 hover:scale-105" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className={`group relative px-4 py-2 font-normal rounded-xl transition-all duration-300 hover:text-brand-blue hover:bg-gray-50/80 tracking-wide ${location.pathname === '/' ? 'text-brand-blue' : 'text-gray-700'}`}>
              Home
              <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-brand-blue to-brand-orange transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
            </Link>
            <Link to="/services" className={`group relative px-4 py-2 font-normal rounded-xl transition-all duration-300 hover:text-brand-blue hover:bg-gray-50/80 tracking-wide ${location.pathname === '/services' ? 'text-brand-blue' : 'text-gray-700'}`}>
              Services
              <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-brand-blue to-brand-orange transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
            </Link>
            <Link to="/about" className={`group relative px-4 py-2 font-normal rounded-xl transition-all duration-300 hover:text-brand-blue hover:bg-gray-50/80 tracking-wide ${location.pathname === '/about' ? 'text-brand-blue' : 'text-gray-700'}`}>
              About
              <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-brand-blue to-brand-orange transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
            </Link>
            <Link to="/portfolio" className={`group relative px-4 py-2 font-normal rounded-xl transition-all duration-300 hover:text-brand-blue hover:bg-gray-50/80 tracking-wide ${location.pathname === '/portfolio' ? 'text-brand-blue' : 'text-gray-700'}`}>
              Portfolio
              <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-brand-blue to-brand-orange transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
            </Link>
            <Link to="/contact" className={`group relative px-4 py-2 font-normal rounded-xl transition-all duration-300 hover:text-brand-blue hover:bg-gray-50/80 tracking-wide ${location.pathname === '/contact' ? 'text-brand-blue' : 'text-gray-700'}`}>
              Contact
              <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-brand-blue to-brand-orange transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
            </Link>
            <Link to="/contact" className="bg-brand-blue text-white px-6 py-2 rounded-xl font-normal hover:bg-brand-orange transition-all duration-300 tracking-wide">
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200/50 p-6 space-y-4">
            <Link to="/" className="flex items-center px-4 py-3 text-gray-700 hover:text-brand-blue hover:bg-gray-50/80 rounded-xl transition-all duration-300 group font-normal tracking-wide" onClick={() => setIsMobileMenuOpen(false)}>
              <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Home
            </Link>
            <Link to="/services" className="flex items-center px-4 py-3 text-gray-700 hover:text-brand-blue hover:bg-gray-50/80 rounded-xl transition-all duration-300 group font-normal tracking-wide" onClick={() => setIsMobileMenuOpen(false)}>
              <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Services
            </Link>
            <Link to="/about" className="flex items-center px-4 py-3 text-gray-700 hover:text-brand-blue hover:bg-gray-50/80 rounded-xl transition-all duration-300 group font-normal tracking-wide" onClick={() => setIsMobileMenuOpen(false)}>
              <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              About
            </Link>
            <Link to="/portfolio" className="flex items-center px-4 py-3 text-gray-700 hover:text-brand-blue hover:bg-gray-50/80 rounded-xl transition-all duration-300 group font-normal tracking-wide" onClick={() => setIsMobileMenuOpen(false)}>
              <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Portfolio
            </Link>
            <Link to="/contact" className="flex items-center px-4 py-3 text-gray-700 hover:text-brand-blue hover:bg-gray-50/80 rounded-xl transition-all duration-300 group font-normal tracking-wide" onClick={() => setIsMobileMenuOpen(false)}>
              <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact
            </Link>
            <Link to="/contact" className="w-full bg-brand-blue text-white py-3 px-6 rounded-xl font-normal hover:bg-brand-orange transition-all duration-300 tracking-wide" onClick={() => setIsMobileMenuOpen(false)}>
              Get Started
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
